import * as React from "react";
import classNames from "classnames";
import { isSameDay } from "date-fns";
import { Dispatch } from "redux";
import { connect } from "react-redux";
import { Route, Redirect, Switch, withRouter } from "react-router-dom";
import { createStyles, WithStyles, Theme, withStyles } from "@material-ui/core";

import * as Api from "../api/index";
import { StoreState } from "../reducers/root-reducer";
import { ConnectedAppBar } from "./app-bar/index";
import { ConnectedDrawer } from "./drawer";
import { InjectedRouterProps } from "../router/props";
import { ConnectedDayOverview } from "./overview/day-overview";
import { ConnectedAdminSettings } from "./admin-settings/index";
import { ConnectedSettings } from "./settings/index";
import { panic, changeLayout, setDate } from "../actions/store/app-actions";
import { ConnectedPanicOverlay } from "./panic-overlay";
import { RealtimeSync } from "../sync";
import { Capabilities } from "../integration/capabilities";
import { ConnectedInquiryList } from "./inquiry-list";
import { ConnectedPlannerList } from "./planner-list";
import { ConnectedAddReservationDialog } from "./overview/add-reservation-dialog";
import { Routes } from "../router/routes";

import { ConnectedPrintviewList } from "./printview";
import { ConnectedDashboard } from "./dashboard";
import { ConnectedPaymentOverview } from "./payments";
import { ConnectedAddPaymentDialog } from './payments/add-payment-dialog';
import { ConnectedEditInquiryDialog } from './inquiry-list/edit-inquiry-dialog';
import { isIPhoneX } from '../util/device-util';

interface State {
	isReady: boolean;
}

export interface MainAppProps {}

interface ConnectedMainAppProps extends WithStyles<typeof styles> {
	paniced: boolean;
	selectedDate: Date;
	capabilities: Capabilities;
	panic: () => void;
	changeLayout: (layout: "desktop" | "tablet" | "phone") => void;
	setDate: (date: Date) => void;
}

type Props = MainAppProps & ConnectedMainAppProps & InjectedRouterProps;

export class MainApp extends React.Component<Props, {}> {
	private rts: RealtimeSync;
	private inactivityTimer: number = null;

	public state: State = {
		isReady: false
	};

	public componentDidMount() {
		const { panic, changeLayout, location, history } = this.props;

		Api.loadInitialData()
			.then(() => {
				this.setState({ isReady: true });
			})
			.catch(r => {
				panic();

				console.error(r);
			});

		this.handleInactivity();

		this.rts = new RealtimeSync();
		this.rts.start();

		const mqTablet = window.matchMedia(
			"(min-width: 960px) and (max-width: 1600px)"
		);
		const mqPhone = window.matchMedia("(max-width: 959px)");

		const check = () => {
			if (mqPhone.matches) {
				changeLayout("phone");

				if (location.pathname === Routes.BookingPlanComposer) {
					history.push(Routes.BookingPlanList);
				}
			} else if (mqTablet.matches) {
				changeLayout("tablet");
			} else {
				changeLayout("desktop");
			}
		};

		mqTablet.addListener(check);
		mqPhone.addListener(check);
		check();
	}

	private handleInactivity = () => {
		let lastActivity = Date.now();
		let timeout = 300000;
		let interval = 10000;

		const resetActivity = () => {
			lastActivity = Date.now();
		};

		const runTimer = () => {
			this.inactivityTimer = setTimeout(() => {
				let elapsed = Date.now() - lastActivity;

				if (elapsed >= timeout) {
					const d = new Date();
					d.setHours(0, 0, 0, 0);

					if (isSameDay(this.props.selectedDate, d) === false) {
						this.props.setDate(d);
					}

					resetActivity();
				}

				runTimer();
			}, interval);
		};

		runTimer();

		document.addEventListener("click", () => {
			resetActivity();
		});
	};

	public componentWillUnmount() {
		this.rts.stop();

		clearTimeout(this.inactivityTimer);
		this.inactivityTimer = null;
	}

	public render() {
		const { capabilities, classes } = this.props;

		return this.state.isReady ? (
			<React.Fragment>
				<div className={classes.root}>
					<ConnectedAppBar />
					<ConnectedDrawer />

					<main className={classes.content}>
						<div className={classes.toolbar} />
						<Switch>
							<Route
								path={Routes.Dashboard}
								component={ConnectedDashboard}
							/>
							<Route
								path={Routes.BookingPlanComposer}
								component={ConnectedDayOverview}
							/>
							<Route
								path={Routes.BookingPlanList}
								component={ConnectedPlannerList}
							/>
							<Route
								path={Routes.InquiryList}
								component={ConnectedInquiryList}
							/>
							<Route
								path={Routes.Payments}
								component={ConnectedPaymentOverview}
							/>
							<Route
								path={Routes.PrintViews}
								component={ConnectedPrintviewList}
							/>
							<Route
								path={Routes.AdminSettings}
								component={ConnectedAdminSettings}
							/>
							<Route
								path={Routes.Settings}
								component={ConnectedSettings}
							/>
							<Route>
								<Redirect to={Routes.BookingPlanComposer} />
							</Route>
						</Switch>
					</main>

					<ConnectedAddReservationDialog />
					<ConnectedAddPaymentDialog />
					<ConnectedEditInquiryDialog />
				</div>
				{this.props.paniced && <ConnectedPanicOverlay />}
			</React.Fragment>
		) : null;
	}
}

const mapStateToProps = (state: StoreState) => ({
	selectedDate: state.date,
	paniced: state.paniced,
	capabilities: state.capabilities
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	panic: () => dispatch(panic()),
	changeLayout: (layout: "desktop" | "tablet" | "phone") =>
		dispatch(changeLayout(layout)),
	setDate: (date: Date) => dispatch(setDate(date))
});

const styles = (theme: Theme) =>
	createStyles({
		root: {
			flexGrow: 1,
			height: "100%",
			zIndex: 1,
			overflow: "hidden",
			position: "relative",
			display: "flex",
			userSelect: "none"
		},
		content: {
			display: "flex",
			flexDirection: "column",
			flexGrow: 1,
			backgroundColor: "transparent",
			padding: 0,
			minWidth: 0
		},
		toolbar: {
			...theme.mixins.toolbar,
			minHeight: 48,

			[theme.breakpoints.up("sm")]: {
				minHeight: 48
			}
		},
	});

export const ConnectedMainApp = withRouter(
	withStyles(styles)(
		connect(
			mapStateToProps,
			mapDispatchToProps
		)(MainApp)
	)
);
